import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Button,
  Stack,
  Avatar,
  Tooltip,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Key as KeyIcon,
  Token as TokenIcon,
  Login as LoginIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";

// ── lang keys used ────────────────────────────────────────────────────────
const L = {
  manageUsers: "Manage Users",
  manageUsersSubtitle: "Change passwords, manage tokens and restrict users",
  search: "Search users...",
  name: "Name",
  email: "Email",
  status: "Status",
  credits: "Credits",
  dailyLimit: "Daily Limit",
  actions: "Actions",
  active: "Active",
  inactive: "Inactive",
  changePassword: "Change Password",
  newPassword: "New Password",
  save: "Save",
  cancel: "Cancel",
  updateCredits: "Update Credits",
  creditsLabel: "Credits",
  expiryLabel: "Expiry Date",
  updateDailyLimit: "Update Daily Token Limit",
  dailyLimitLabel: "Daily Token Limit",
  dailyLimitHint: "Set 0 for unlimited",
  autoLogin: "Auto Login",
  autoLoginConfirm: "Login as this user?",
  confirm: "Confirm",
  restrict: "Restrict User",
  activate: "Activate User",
  noUsers: "No users found",
  tokensLeft: "Tokens Left",
};

// ── Small stat chip ───────────────────────────────────────────────────────
const StatChip = ({ label, value, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 2,
        bgcolor: alpha(color || theme.palette.primary.main, 0.1),
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: color || "primary.main" }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {label}
      </Typography>
    </Box>
  );
};

const ManageUsers = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => lang?.[k] || ctxLang?.[k] || L[k] || k;

  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Dialog states ─────────────────────────────────────────────────────
  const [pwdDialog, setPwdDialog] = useState({
    open: false,
    user: null,
    value: "",
  });
  const [creditsDialog, setCreditsDialog] = useState({
    open: false,
    user: null,
    value: "",
    expiry: "",
  });
  const [dailyDialog, setDailyDialog] = useState({
    open: false,
    user: null,
    value: "",
  });
  const [autoLoginDialog, setAutoLoginDialog] = useState({
    open: false,
    user: null,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(users);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(
      users.filter(
        (u) =>
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q),
      ),
    );
  }, [search, users]);

  async function fetchUsers() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/admin/users/list",
      admin: true,
      post: false,
      showLoading: false,
    });
    setLoading(false);
    if (res?.data?.success) setUsers(res.data.data || []);
  }

  // ── Change password ───────────────────────────────────────────────────
  async function handleChangePassword() {
    if (!pwdDialog.value || pwdDialog.value.length < 6) return;
    const res = await hitAxios({
      path: "/api/admin/users/edit-password",
      admin: true,
      post: true,
      obj: { uid: pwdDialog.user.uid, password: pwdDialog.value },
    });
    if (res?.data?.success)
      setPwdDialog({ open: false, user: null, value: "" });
  }

  // ── Toggle status ─────────────────────────────────────────────────────
  async function handleToggleStatus(user) {
    const newStatus = user.status === "active" ? "inactive" : "active";
    const res = await hitAxios({
      path: "/api/admin/users/toggle-status",
      admin: true,
      post: true,
      obj: { uid: user.uid, status: newStatus },
    });
    if (res?.data?.success) {
      setUsers((prev) =>
        prev.map((u) => (u.uid === user.uid ? { ...u, status: newStatus } : u)),
      );
    }
  }

  // ── Update credits ────────────────────────────────────────────────────
  async function handleUpdateCredits() {
    const res = await hitAxios({
      path: "/api/admin/users/edit-credits",
      admin: true,
      post: true,
      obj: {
        uid: creditsDialog.user.uid,
        credits: creditsDialog.value,
        credit_expiry: creditsDialog.expiry || null,
      },
    });
    if (res?.data?.success) {
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === creditsDialog.user.uid
            ? {
                ...u,
                credits: creditsDialog.value,
                credit_expiry: creditsDialog.expiry,
              }
            : u,
        ),
      );
      setCreditsDialog({ open: false, user: null, value: "", expiry: "" });
    }
  }

  // ── Update daily limit ────────────────────────────────────────────────
  async function handleUpdateDailyLimit() {
    const res = await hitAxios({
      path: "/api/admin/users/update-daily-limit",
      admin: true,
      post: true,
      obj: { uid: dailyDialog.user.uid, daily_token_limit: dailyDialog.value },
    });
    if (res?.data?.success) {
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === dailyDialog.user.uid
            ? { ...u, daily_token_limit: dailyDialog.value }
            : u,
        ),
      );
      setDailyDialog({ open: false, user: null, value: "" });
    }
  }

  // ── Auto login ────────────────────────────────────────────────────────
  async function handleAutoLogin() {
    const res = await hitAxios({
      path: "/api/admin/users/auto-login",
      admin: true,
      post: true,
      obj: { uid: autoLoginDialog.user.uid },
      showSnackbar: false,
    });
    if (res?.data?.success) {
      localStorage.setItem(
        process.env.REACT_APP_TOKEN + "_user",
        res.data.token,
      );
      window.open("/user", "_blank");
    }
    setAutoLoginDialog({ open: false, user: null });
  }

  return (
    <Box>
      <PageHeader
        title={t("manageUsers")}
        subtitle={t("manageUsersSubtitle")}
        icon={PeopleIcon}
      />

      {/* ── Search bar ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={t("search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* ── Table ── */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  t("name"),
                  t("email"),
                  t("status"),
                  t("credits"),
                  t("dailyLimit"),
                  t("actions"),
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, fontSize: "0.75rem", py: 1.5 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "text.disabled" }}
                  >
                    {loading ? "Loading..." : t("noUsers")}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((user) => (
                  <TableRow key={user.uid} hover>
                    {/* Name */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: "primary.main",
                          }}
                        >
                          {user.name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ maxWidth: 120 }}
                        >
                          {user.name || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Email */}
                    <TableCell>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ maxWidth: 160, display: "block" }}
                      >
                        {user.email}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={
                          user.status === "active" ? t("active") : t("inactive")
                        }
                        size="small"
                        color={user.status === "active" ? "success" : "default"}
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                    </TableCell>

                    {/* Credits */}
                    <TableCell>
                      <StatChip
                        label={t("credits")}
                        value={Number(user.credits || 0).toLocaleString()}
                        color={theme.palette.info.main}
                      />
                    </TableCell>

                    {/* Daily Limit */}
                    <TableCell>
                      <StatChip
                        label={t("dailyLimit")}
                        value={
                          user.daily_token_limit > 0
                            ? Number(user.daily_token_limit).toLocaleString()
                            : "∞"
                        }
                        color={
                          user.daily_token_limit > 0
                            ? theme.palette.warning.main
                            : theme.palette.success.main
                        }
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {/* Change Password */}
                        <Tooltip title={t("changePassword")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPwdDialog({ open: true, user, value: "" })
                            }
                            sx={{
                              color: "primary.main",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <KeyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>

                        {/* Update Credits */}
                        <Tooltip title={t("updateCredits")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setCreditsDialog({
                                open: true,
                                user,
                                value: String(user.credits || 0),
                                expiry: user.credit_expiry || "",
                              })
                            }
                            sx={{
                              color: "info.main",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                              },
                            }}
                          >
                            <TokenIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>

                        {/* Daily Limit */}
                        <Tooltip title={t("updateDailyLimit")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDailyDialog({
                                open: true,
                                user,
                                value: String(user.daily_token_limit || 0),
                              })
                            }
                            sx={{
                              color: "warning.main",
                              "&:hover": {
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                              },
                            }}
                          >
                            <TodayIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>

                        {/* Toggle Status */}
                        <Tooltip
                          title={
                            user.status === "active"
                              ? t("restrict")
                              : t("activate")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(user)}
                            sx={{
                              color:
                                user.status === "active"
                                  ? "error.main"
                                  : "success.main",
                              "&:hover": {
                                bgcolor: alpha(
                                  user.status === "active"
                                    ? theme.palette.error.main
                                    : theme.palette.success.main,
                                  0.1,
                                ),
                              },
                            }}
                          >
                            {user.status === "active" ? (
                              <LockIcon sx={{ fontSize: 15 }} />
                            ) : (
                              <LockOpenIcon sx={{ fontSize: 15 }} />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* Auto Login */}
                        <Tooltip title={t("autoLogin")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setAutoLoginDialog({ open: true, user })
                            }
                            sx={{
                              color: "secondary.main",
                              "&:hover": {
                                bgcolor: alpha(
                                  theme.palette.secondary.main,
                                  0.1,
                                ),
                              },
                            }}
                          >
                            <LoginIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ══ Change Password Dialog ══ */}
      <CommonDialog
        open={pwdDialog.open}
        onClose={() => setPwdDialog({ open: false, user: null, value: "" })}
        title={t("changePassword")}
        subtitle={pwdDialog.user?.email}
        icon={KeyIcon}
        maxWidth="xs"
      >
        <Stack spacing={3} mt={1}>
          <TextField
            fullWidth
            autoFocus
            label={t("newPassword")}
            type="password"
            value={pwdDialog.value}
            onChange={(e) =>
              setPwdDialog((p) => ({ ...p, value: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
            helperText={
              pwdDialog.value.length > 0 && pwdDialog.value.length < 6
                ? "Min 6 characters"
                : ""
            }
            error={pwdDialog.value.length > 0 && pwdDialog.value.length < 6}
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() =>
                setPwdDialog({ open: false, user: null, value: "" })
              }
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleChangePassword}
              disabled={!pwdDialog.value || pwdDialog.value.length < 6}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>

      {/* ══ Update Credits Dialog ══ */}
      <CommonDialog
        open={creditsDialog.open}
        onClose={() =>
          setCreditsDialog({ open: false, user: null, value: "", expiry: "" })
        }
        title={t("updateCredits")}
        subtitle={creditsDialog.user?.email}
        icon={TokenIcon}
        maxWidth="xs"
      >
        <Stack spacing={3} mt={1}>
          <TextField
            fullWidth
            autoFocus
            label={t("creditsLabel")}
            type="number"
            value={creditsDialog.value}
            onChange={(e) =>
              setCreditsDialog((p) => ({ ...p, value: e.target.value }))
            }
            inputProps={{ min: 0 }}
          />
          <TextField
            fullWidth
            label={t("expiryLabel")}
            type="date"
            value={creditsDialog.expiry}
            onChange={(e) =>
              setCreditsDialog((p) => ({ ...p, expiry: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() =>
                setCreditsDialog({
                  open: false,
                  user: null,
                  value: "",
                  expiry: "",
                })
              }
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateCredits}
              disabled={creditsDialog.value === ""}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>

      {/* ══ Daily Token Limit Dialog ══ */}
      <CommonDialog
        open={dailyDialog.open}
        onClose={() => setDailyDialog({ open: false, user: null, value: "" })}
        title={t("updateDailyLimit")}
        subtitle={dailyDialog.user?.email}
        icon={TodayIcon}
        maxWidth="xs"
      >
        <Stack spacing={3} mt={1}>
          <TextField
            fullWidth
            autoFocus
            label={t("dailyLimitLabel")}
            type="number"
            value={dailyDialog.value}
            onChange={(e) =>
              setDailyDialog((p) => ({ ...p, value: e.target.value }))
            }
            helperText={t("dailyLimitHint")}
            inputProps={{ min: 0 }}
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() =>
                setDailyDialog({ open: false, user: null, value: "" })
              }
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateDailyLimit}
              disabled={dailyDialog.value === ""}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>

      {/* ══ Auto Login Confirm Dialog ══ */}
      <CommonDialog
        open={autoLoginDialog.open}
        onClose={() => setAutoLoginDialog({ open: false, user: null })}
        title={t("autoLogin")}
        subtitle={autoLoginDialog.user?.email}
        icon={LoginIcon}
        maxWidth="xs"
      >
        <Stack spacing={3} mt={1}>
          <Typography variant="body2" color="text.secondary">
            {t("autoLoginConfirm")}{" "}
            <strong>{autoLoginDialog.user?.name}</strong>?{" "}
            {lang?.autoLoginNote ||
              "This will open a new tab logged in as this user."}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setAutoLoginDialog({ open: false, user: null })}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAutoLogin}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("confirm")}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>
    </Box>
  );
};

export default ManageUsers;
